import { Fragment, type ReactNode, useEffect, useMemo, useRef } from "react";

import { clsx, Text } from "@alpacahq/ui/old";

import { useLock } from "~/v2/api/hooks";
import type { OptionContract, Quote } from "~/v2/api/rest/data";
import { getPrettyAmount } from "~/v2/utils/formatting";
import type { ViewState } from "..";
import { OptionChainButton } from "./OptionChainButton";
import type { OptionContractWithSnapshot } from "./useGetOptionsData";

const ROW_HEIGHT = 32; // className is h-8

type SideBySideColumn = {
  key: string;
  label: string;
  accessor: (
    c: GroupedContract,
    onClick?: (c: Contract, price?: number, type?: string) => void,
  ) => ReactNode;
  type?: string;
};

type Column = {
  label: string;
  accessor: (
    c?: Contract,
    onClick?: (c: Contract, price?: number, type?: string) => void,
  ) => ReactNode;
  type?: string;
};

type ColumnAccessorProps = Pick<
  Contract,
  "close_price" | "strike_price" | "type"
>;

// Use numbers instead of strings to make this easier to organize and sort
type Contract = Omit<
  OptionContractWithSnapshot,
  "strike_price" | "close_price"
> & {
  strike_price: number;
  close_price: number;
};

export const toNumberContract = (optionContract: OptionContract): Contract => ({
  ...optionContract,
  strike_price: parseFloat(optionContract.strike_price),
  close_price: parseFloat(optionContract.close_price ?? "0"),
});

export const toStringContract = (
  contract: Contract,
): OptionContractWithSnapshot => ({
  ...contract,
  strike_price: `${contract.strike_price}`,
  close_price: `${contract.close_price}`,
});

type GroupedContract = {
  call?: Contract;
  put?: Contract;
};

export const getBreakeven = ({
  close_price,
  strike_price,
  type,
}: ColumnAccessorProps) => {
  if (type === "call") {
    return getPrettyAmount(strike_price + close_price);
  }

  return getPrettyAmount(strike_price - close_price);
};

export const getBreakevenPercent = ({
  price,
  type,
  close_price,
  strike_price,
}: ColumnAccessorProps & { price: number }) =>
  `${(
    ((parseFloat(getBreakeven({ type, close_price, strike_price })) - price) /
      price) *
      100
  ).toFixed(2)}%`;

export interface OptionsChainProps {
  quote: Quote | undefined;
  optionContracts: OptionContract[];
  isLoading: boolean;
  expiration: string | undefined;
  optionsType: ViewState["optionType"];
  onClick: (contract: OptionContract, price?: number, type?: string) => void;
  selectedContracts?: { symbol: string; type: string }[];
}

export const OptionChain = ({
  quote,
  optionContracts,
  isLoading,
  expiration,
  optionsType,
  onClick,
  selectedContracts = [],
}: OptionsChainProps) => {
  const scrollLock = useLock({ initialLocked: true });

  const groupedContracts = useMemo(() => {
    const contracts = optionContracts
      // Convert to Contract type with strike_price/close_price as number
      .map(toNumberContract)
      // Group contracts by strike price
      .reduce(
        (acc, currentContract) => {
          const existing = acc[currentContract.strike_price] ?? {};
          acc[currentContract.strike_price] = {
            ...existing,
            [currentContract.type]: currentContract,
          };
          return acc;
        },
        {} as { [strikePrice: number]: GroupedContract },
      );

    // Convert to array, sort by strike price
    return Object.entries(contracts)
      .map(([strikePrice, groupedContract]) => ({
        strikePrice: parseFloat(strikePrice),
        ...groupedContract,
      }))
      .sort((a, b) => a.strikePrice - b.strikePrice);
  }, [optionContracts]);

  const price = quote?.last ?? 0;

  // Used for scrolling to the ATM contract
  const wrapperRef = useRef<HTMLDivElement>(null);

  const onContractClicked = (c: Contract, price?: number, type?: string) =>
    onClick(toStringContract(c), price, type);

  const singleColumns: Column[] = [
    {
      label: "Strike",
      // Strike price is same value for Call and Put
      accessor: (c) => getPrettyAmount(c?.strike_price ?? "-"),
    },
    {
      label: "Last",
      accessor: (c) =>
        getPrettyAmount(c?.snapshot?.latestTrade?.p ?? c?.close_price ?? "-"),
    },
    {
      label: "Bid",
      accessor: (contract, onClick) => (
        <OptionChainButton
          disabled={!contract}
          onClick={() => {
            if (contract) {
              onClick?.(contract, contract?.snapshot?.latestQuote?.bp, "bid");
            }
          }}
          type="bid"
          isSelected={
            !!selectedContracts.find(
              ({ symbol, type }) =>
                symbol === contract?.symbol && type === "bid",
            )
          }
        >
          {getPrettyAmount(contract?.snapshot?.latestQuote?.bp ?? "-")}
        </OptionChainButton>
      ),
    },
    {
      label: "Ask",
      accessor: (contract, onClick) => (
        <OptionChainButton
          disabled={!contract}
          onClick={() => {
            if (contract) {
              onClick?.(contract, contract?.snapshot?.latestQuote?.ap, "ask");
            }
          }}
          type="ask"
          isSelected={
            !!selectedContracts.find(
              ({ symbol, type }) =>
                symbol === contract?.symbol && type === "ask",
            )
          }
        >
          {getPrettyAmount(contract?.snapshot?.latestQuote?.ap ?? "-")}
        </OptionChainButton>
      ),
    },
    {
      label: "Change",
      // @todo Make sure this is the correct calculation
      accessor: (c) =>
        getPrettyAmount(
          c != null
            ? (c?.snapshot?.latestTrade?.p ?? c?.close_price) - c?.close_price
            : "-",
        ),
    },
    {
      label: "Change (%)",
      // @todo Make sure this is the correct calculation
      accessor: (c) =>
        c?.close_price
          ? `${(
              (((c?.snapshot?.latestTrade?.p ?? c?.close_price) -
                c?.close_price) /
                c?.close_price) *
                100
            ).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}%`
          : " - ",
    },
    {
      label: "Implied Volatility",
      accessor: (c) =>
        c?.snapshot?.impliedVolatility
          ? `${(c?.snapshot?.impliedVolatility * 100).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}%`
          : " - ",
    },
    {
      label: "Delta",
      accessor: (c) =>
        (c?.snapshot?.greeks?.delta ?? " - ").toLocaleString("en-US", {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        }),
    },
    {
      label: "Volume",
      accessor: (c) => c?.snapshot?.dailyBar?.v ?? "-",
    },
    {
      label: "Open Interest",
      accessor: (c) => getPrettyAmount(c?.open_interest ?? "-", 0),
    },
  ];

  const sideBySideColumns: SideBySideColumn[] = [
    {
      key: "oi",
      label: "OI",
      type: "call",
      accessor: ({ call }) => getPrettyAmount(call?.open_interest ?? "-", 0),
    },
    {
      key: "volume",
      label: "Vol",
      type: "call",
      accessor: ({ call }) => call?.snapshot?.dailyBar?.v ?? "-",
    },
    {
      key: "delta",
      label: "Delta",
      type: "call",
      accessor: ({ call }) =>
        (call?.snapshot?.greeks?.delta ?? " - ").toLocaleString("en-US", {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        }),
    },
    {
      key: "iv",
      label: "IV",
      type: "call",
      accessor: ({ call }) =>
        call?.snapshot?.impliedVolatility
          ? `${(call?.snapshot?.impliedVolatility * 100).toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              },
            )}%`
          : " - ",
    },
    {
      key: "chg",
      label: "Chg",
      type: "call",
      // @todo Make sure this is the correct calculation
      accessor: ({ call }) =>
        getPrettyAmount(
          call != null
            ? (call?.snapshot?.latestTrade?.p ?? call?.close_price) -
                call?.close_price
            : "-",
        ),
    },
    {
      key: "bid",
      label: "Bid",
      type: "call",
      accessor: ({ call }, onClick) => (
        <OptionChainButton
          disabled={!call}
          onClick={() => {
            if (call) {
              onClick?.(call, call?.snapshot?.latestQuote?.bp, "bid");
            }
          }}
          type="bid"
          isSelected={
            !!selectedContracts.find(
              ({ symbol, type }) => symbol === call?.symbol && type === "bid",
            )
          }
        >
          {/* @todo Add this property to the OptionContract type when available */}
          {getPrettyAmount(call?.snapshot?.latestQuote?.bp ?? "-")}
        </OptionChainButton>
      ),
    },
    {
      key: "ask",
      label: "Ask",
      type: "call",
      accessor: ({ call }, onClick) => (
        <OptionChainButton
          disabled={!call}
          onClick={() => {
            if (call) {
              onClick?.(call, call?.snapshot?.latestQuote?.ap, "ask");
            }
          }}
          type="ask"
          isSelected={
            !!selectedContracts.find(
              ({ symbol, type }) => symbol === call?.symbol && type === "ask",
            )
          }
        >
          {/* @todo Add this property to the OptionContract type when available */}
          {getPrettyAmount(call?.snapshot?.latestQuote?.ap ?? "-")}
        </OptionChainButton>
      ),
    },
    {
      key: "last",
      label: "Last",
      type: "call",
      accessor: ({ call }) =>
        getPrettyAmount(
          call?.snapshot?.latestTrade?.p ?? call?.close_price ?? "-",
        ),
    },
    {
      key: "strike",
      label: "Strike",
      // Strike price is same value for Call and Put
      accessor: ({ call }) => <div>{getPrettyAmount(call?.strike_price)}</div>,
    },
    {
      key: "last-2",
      label: "Last",
      type: "put",
      accessor: ({ put }) =>
        getPrettyAmount(
          put?.snapshot?.latestTrade?.p ?? put?.close_price ?? "-",
        ),
    },
    {
      key: "bid-2",
      label: "Bid",
      type: "put",
      accessor: ({ put }, onClick) => (
        <OptionChainButton
          disabled={!put}
          onClick={() => {
            if (put) {
              onClick?.(put, put?.snapshot?.latestQuote?.bp, "bid");
            }
          }}
          type="bid"
          isSelected={
            !!selectedContracts.find(
              ({ symbol, type }) => symbol === put?.symbol && type === "bid",
            )
          }
        >
          {getPrettyAmount(put?.snapshot?.latestQuote?.bp ?? "-")}
        </OptionChainButton>
      ),
    },
    {
      key: "ask-2",
      label: "Ask",
      type: "put",
      accessor: ({ put }, onClick) => (
        <OptionChainButton
          disabled={!put}
          onClick={() => {
            if (put) {
              onClick?.(put, put?.snapshot?.latestQuote?.ap, "ask");
            }
          }}
          type="ask"
          isSelected={
            !!selectedContracts.find(
              ({ symbol, type }) => symbol === put?.symbol && type === "ask",
            )
          }
        >
          {getPrettyAmount(put?.snapshot?.latestQuote?.ap ?? "-")}
        </OptionChainButton>
      ),
    },
    {
      key: "chg-2",
      label: "Chg",
      type: "put",
      // @todo Make sure this is the correct calculation
      accessor: ({ put }) =>
        getPrettyAmount(
          put != null
            ? (put?.snapshot?.latestTrade?.p ?? put?.close_price) -
                put?.close_price
            : "-",
        ),
    },
    {
      key: "iv2",
      label: "IV",
      type: "put",
      accessor: ({ put }) =>
        put?.snapshot?.impliedVolatility
          ? `${(put?.snapshot?.impliedVolatility * 100).toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              },
            )}%`
          : " - ",
    },
    {
      key: "delta2",
      label: "Delta",
      type: "put",
      accessor: ({ put }) =>
        (put?.snapshot?.greeks?.delta ?? " - ").toLocaleString("en-US", {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        }),
    },
    {
      key: "volume-2",
      label: "Vol",
      type: "put",
      accessor: ({ put }) => put?.snapshot?.dailyBar?.v ?? "-",
    },
    {
      key: "oi-2",
      label: "OI",
      type: "put",
      accessor: ({ put }) => getPrettyAmount(put?.open_interest ?? "-", 0),
    },
  ];

  // biome-ignore lint/correctness/useExhaustiveDependencies: selectedContracts is listed to refresh the column closures: singleColumns and sideBySideColumns are array literals rebuilt every render whose cell renderers close over selectedContracts, so listing them would rebuild the chain columns every render, while dropping selectedContracts would freeze the selection highlight at its first-render value. Memoizing the two column arrays is the real fix and is left for a follow-up.
  const columns = useMemo(
    () => (optionsType === "Calls & Puts" ? sideBySideColumns : singleColumns),
    [optionsType, selectedContracts],
  );

  useEffect(() => {
    if (
      // The scroll lock is already locked
      // The wrapperRef is unavailable
      // There are no contracts to scroll to
      !(
        !scrollLock.locked &&
        wrapperRef.current &&
        Object.keys(groupedContracts)?.length
      )
    ) {
      return;
    }

    let atmIndex = -1;
    const values = Object.values(groupedContracts);

    if (optionsType !== "Puts") {
      atmIndex =
        values.findIndex(
          // First contract with a strike price greater than or equal to the current price
          ({ call }) => call && call?.strike_price >= price,
        ) - 1;

      atmIndex = Math.max(atmIndex, 0);
    } else {
      for (let i = values.length - 1; i >= 0; i--) {
        // First contract with a strike price less than or equal to the current price
        const put = values[i].put;
        if (put && put?.strike_price <= price) {
          atmIndex = i;
          break;
        }
      }
    }

    // If the ATM contract is not found, don't scroll
    if (atmIndex === -1) {
      return;
    }

    const elements = wrapperRef.current.getElementsByTagName("tr");
    const row = elements[atmIndex];

    if (row) {
      // puts underlying strike price in center of table
      const offset = row.offsetTop - ROW_HEIGHT * 6 - 2;

      wrapperRef.current.scrollTo({
        top: offset,
        behavior: "smooth",
      });

      // Prevent potential UX glitches on re-renders
      scrollLock.lock();
    }
  }, [groupedContracts, scrollLock, optionsType, price]);

  // Unlock if view or expiration changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: optionsType and expiration are the triggers: scroll lock is released when the user changes chain view. scrollLock.unlock is recreated every render, so listing it would release the lock on every render.
  useEffect(() => {
    scrollLock.unlock();
  }, [optionsType, expiration]);

  return (
    <div className="optionChain relative">
      <div
        ref={wrapperRef}
        className="max-h-[520px] overflow-x-auto overflow-y-scroll"
      >
        <table className="w-full table-auto whitespace-nowrap text-xs">
          <thead className="sticky top-0 z-20 bg-white">
            {optionsType === "Calls & Puts" && (
              <>
                <tr className="bg-white">
                  <td className="bg-gray-50 p-0" colSpan={8}>
                    <div className="flex h-8 items-center justify-center px-2">
                      <Text className="text-center text-sm">Calls</Text>
                    </div>
                  </td>
                  <td />
                  <td className="bg-gray-50 p-0" colSpan={8}>
                    <div className="flex h-8 items-center justify-center px-2">
                      <Text className="text-center text-sm">Puts</Text>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="h-4" colSpan={100} />
                </tr>
              </>
            )}
            <tr className="bg-white font-medium text-zinc-900">
              {columns.map((column) => (
                <th
                  key={"key" in column ? column.key : column.label}
                  className={"h-8 bg-white p-0 text-right font-medium text-xs"}
                >
                  <div
                    className={clsx(
                      "flex h-8 items-center border-zinc-100 border-b px-2",
                      column.label === "Strike"
                        ? "justify-center"
                        : "justify-end",
                    )}
                  >
                    {column.label}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!optionContracts.length ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="zinc-500 h-8 text-center"
                >
                  {isLoading
                    ? "Loading Option Contracts..."
                    : "No contracts found"}
                </td>
              </tr>
            ) : (
              groupedContracts.map(
                ({ strikePrice, call, put }, index, array) => {
                  const nextStrikePrice =
                    array[index + 1]?.strikePrice || strikePrice;

                  const inTheMoney =
                    optionsType !== "Puts"
                      ? strikePrice < price
                      : strikePrice > price;

                  const columnData =
                    optionsType === "Calls & Puts"
                      ? { call, put }
                      : optionsType === "Calls"
                        ? call
                        : put;

                  return (
                    <Fragment key={`${strikePrice}-${call?.id}-${put?.id}`}>
                      <tr
                        className={clsx(
                          "border-gray-200 border-b bg-white hover:brightness-95",
                          inTheMoney &&
                            optionsType !== "Calls & Puts" &&
                            "bg-blue-50",
                        )}
                      >
                        {columns.map((column) => {
                          return (
                            <td
                              key={"key" in column ? column.key : column.label}
                              className="h-fit p-0 text-xs"
                            >
                              <div
                                className={clsx(
                                  "flex h-8 items-center justify-end px-2 tabular-nums",
                                  column.label === "Strike" && "justify-center",
                                  index === 0 &&
                                    "border-l-transparent pl-[6px]",
                                  ((optionsType === "Calls & Puts" &&
                                    column.type === "call" &&
                                    strikePrice < price) ||
                                    (column.type === "put" &&
                                      strikePrice > price)) &&
                                    "bg-blue-50",
                                  optionsType === "Calls & Puts" &&
                                    column.label === "Strike" &&
                                    "border-r border-l",
                                )}
                              >
                                {/* columnData and the column list are chosen by the same optionsType check,
                                    so this is correct at runtime, but TypeScript cannot correlate the two
                                    ternaries: calling the union of both accessor signatures demands
                                    GroupedContract & Contract, which nothing satisfies. The directive has to
                                    sit on its own line directly above — TypeScript records a block-comment
                                    directive at the comment's first line, so a multi-line one suppresses
                                    nothing. */}
                                {/* @ts-expect-error TS2345, see above */}
                                {column.accessor(columnData, onContractClicked)}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                      {strikePrice <= price && price < nextStrikePrice && (
                        <tr className="bg-gray-100">
                          <td className="h-fit p-0" colSpan={100}>
                            <div className="flex h-8 items-center justify-center px-2">
                              <Text className="text-center text-gray-800 text-xs">
                                Underlying Share Price{" "}
                                <span className="font-bold">
                                  {getPrettyAmount(price)}
                                </span>
                              </Text>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                },
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
